import { useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import React, { useState, useEffect, useRef } from "react";
import { companyAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import LogoIcon from "./LogoIcon";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  const searchRef = useRef(null);

  // Show search box on inner pages, not on home
  const showSearchInNav = location.pathname !== "/";

  // Debounced search
  useEffect(() => {
    if (!showSearchInNav) return;

    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 1) {
        companyAPI
          .searchCompanies(searchQuery)
          .then((list) => {
            setSearchResults(list);
            setShowDropdown(list.length > 0);
          })
          .catch((err) => console.error("Navbar search error:", err));
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery, showSearchInNav]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  const handleSelectCompany = (item) => {
    if (!item.symbol) return;
    navigate(`/company/${item.symbol.toUpperCase()}`);
    setSearchQuery("");
    setShowDropdown(false);
    setMobileSearchOpen(false);
  };

  // Mobile menu icons (you can replace with react-icons or custom SVG)
  const SearchIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <path d="m21 21-4.35-4.35"></path>
    </svg>
  );

  const MenuIcon = () => (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );

  const CloseIcon = () => (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  return (
    <header className="sa-header">
      <div className="sa-header-inner">
        {/* Logo */}
        <div className="sa-logo" onClick={() => navigate("/")}>
          <div className="sa-logo-mark">
          <LogoIcon />
          </div>
          <span className="sa-logo-text">StockAnalyzer</span>
        </div>

        {/* Desktop Search */}
        {showSearchInNav && (
          <div className="sa-nav-search sa-desktop-search" ref={searchRef}>
            <input
              type="text"
              className="sa-nav-search-input"
              placeholder="Search for a company"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            />

            {/* Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="sa-nav-dropdown">
                {searchResults.map((item, index) => (
                  <div
                    key={index}
                    className="sa-nav-dropdown-item"
                    onClick={() => handleSelectCompany(item)}
                  >
                    <span className="sa-nav-dropdown-name">{item.name}</span>
                    <span className="sa-nav-dropdown-symbol">{item.symbol}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop Right Side
        <div className="sa-right sa-desktop-right">
          {isAuthenticated ? (
            <>
              {/* <span className="sa-user-name">Hi, {user?.name}</span> */}
              {/* <span className="sa-user-name">Welcome</span>
              <button className="sa-primary-btn" onClick={logout}>
                Logout
              </button> */}
            {/* </> */}
          {/* ) : (
            <>
              <button className="sa-login-link" onClick={() => navigate("/login")}>
                Login
              </button>
              <button className="sa-primary-btn" onClick={() => navigate("/register")}>
                Get Free Account
              </button>
            </>
          )}
        </div> */} 

        {/* Mobile Right Side */}
        <div className="sa-mobile-right">
          {showSearchInNav && (
            <button
              className="sa-mobile-icon-btn"
              onClick={() => {
                setMobileSearchOpen(!mobileSearchOpen);
                setMobileMenuOpen(false);
              }}
              aria-label="Toggle search"
            >
              <SearchIcon />
            </button>
          )}

          <button
            className="sa-mobile-icon-btn"
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setMobileSearchOpen(false);
            }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {showSearchInNav && mobileSearchOpen && (
        <div className="sa-mobile-search-container" ref={searchRef}>
          <input
            type="text"
            className="sa-mobile-search-input"
            placeholder="Search for a company"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />

          {showDropdown && searchResults.length > 0 && (
            <div className="sa-nav-dropdown">
              {searchResults.map((item, index) => (
                <div
                  key={index}
                  className="sa-nav-dropdown-item"
                  onClick={() => handleSelectCompany(item)}
                >
                  <span className="sa-nav-dropdown-name">{item.name}</span>
                  <span className="sa-nav-dropdown-symbol">{item.symbol}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mobile Menu
      {mobileMenuOpen && (
        <div className="sa-mobile-menu">
          {isAuthenticated ? (
            <>
              <div className="sa-mobile-user">Hi, {user?.name}</div>
              <button className="sa-mobile-menu-btn sa-primary-btn" onClick={logout}>
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                className="sa-mobile-menu-btn sa-login-link"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                className="sa-mobile-menu-btn sa-primary-btn"
                onClick={() => navigate("/register")}
              >
                Get Free Account
              </button>
            </>
          )}
        </div>
      )} */}
    </header>
  );
};

export default Navbar;